<template>
  <Card>
    <CardHeader>
      <CardTitle>1. Create or select a retailer</CardTitle>
      <CardDescription>
        Search for an existing retailer to reuse it, or create a brand new one.
      </CardDescription>
    </CardHeader>

    <CardContent class="space-y-5">
      <!-- Mode toggle -->
      <div class="inline-flex rounded-lg border border-border p-1 bg-muted/40">
        <button
          type="button"
          @click="mode = 'select'"
          class="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
          :class="mode === 'select' ? 'bg-background shadow-sm' : 'text-muted-foreground'"
        >
          Select existing
        </button>
        <button
          type="button"
          @click="mode = 'create'"
          class="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
          :class="mode === 'create' ? 'bg-background shadow-sm' : 'text-muted-foreground'"
        >
          Create new
        </button>
      </div>

      <!-- SELECT EXISTING -->
      <div v-if="mode === 'select'" class="space-y-3">
        <!-- Currently selected retailer (persists across refresh) with a way to clear it -->
        <div
          v-if="wizard.selectedRetailer"
          class="flex items-center gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3"
        >
          <img
            v-if="wizard.retailer.retailerLogo"
            :src="wizard.retailer.retailerLogo"
            class="object-contain w-10 h-10 border border-border bg-white rounded-lg shrink-0"
          />
          <div
            v-else
            class="flex items-center justify-center w-10 h-10 border border-border rounded-lg bg-muted shrink-0"
          >
            <Store class="w-5 h-5 text-muted-foreground" />
          </div>
          <div class="min-w-0">
            <p class="text-sm font-medium truncate">{{ wizard.retailer.retailerName }}</p>
            <p class="text-xs text-muted-foreground">Selected retailer</p>
          </div>
          <Button variant="ghost" size="sm" class="ml-auto shrink-0" @click="deselect">
            <X class="w-4 h-4" />
            Remove
          </Button>
        </div>

        <div class="relative">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            v-model="search"
            placeholder="Search retailers by name"
            class="pl-9"
            @keyup.enter="runSearch"
          />
        </div>

        <div v-if="loadingSearch" class="flex items-center justify-center py-10">
          <Loader2 class="w-6 h-6 animate-spin text-muted-foreground" />
        </div>

        <div v-else-if="results.length" class="divide-y divide-border rounded-lg border border-border">
          <button
            v-for="r in results"
            :key="r.id"
            type="button"
            @click="selectRetailer(r)"
            class="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
            :class="wizard.selectedRetailer && wizard.retailer.id === r.id ? 'bg-muted/60' : ''"
          >
            <img
              v-if="r.logo"
              :src="r.logo"
              class="object-contain w-10 h-10 border border-border bg-white rounded-lg shrink-0"
            />
            <div
              v-else
              class="flex items-center justify-center w-10 h-10 border border-border rounded-lg bg-muted shrink-0"
            >
              <Store class="w-5 h-5 text-muted-foreground" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-medium truncate">{{ r.retailerName }}</p>
              <p class="text-xs text-muted-foreground truncate">{{ r.retailerWebsite }}</p>
            </div>
            <Badge
              v-if="r.openBankingPublisherCompatible"
              variant="secondary"
              class="ml-auto shrink-0"
            >
              Open banking
            </Badge>
            <Check
              v-if="wizard.selectedRetailer && wizard.retailer.id === r.id"
              class="w-5 h-5 text-primary shrink-0"
              :class="r.openBankingPublisherCompatible ? '' : 'ml-auto'"
            />
          </button>
        </div>

        <div
          v-else-if="hasSearched"
          class="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground"
        >
          <Store class="w-8 h-8" />
          <p class="text-sm">No retailers found for "{{ search }}".</p>
        </div>
      </div>

      <!-- CREATE NEW -->
      <div v-else class="grid gap-5 sm:grid-cols-2">
        <div class="space-y-1.5">
          <Label>Retailer name</Label>
          <Input v-model="form.retailerName" placeholder="e.g. Acme Outdoors" />
        </div>
        <div class="space-y-1.5">
          <Label>Website</Label>
          <Input v-model="form.retailerWebsite" placeholder="https://example.com" />
        </div>
        <div class="space-y-1.5">
          <Label>Monthly web visits</Label>
          <Input v-model.number="form.monthlyWebVisits" type="number" placeholder="0" />
        </div>
        <div class="space-y-1.5">
          <Label>Logo</Label>
          <input
            ref="logoInput"
            type="file"
            accept="image/*"
            class="block w-full cursor-pointer text-sm text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-foreground file:transition-colors hover:file:bg-primary/90"
            @change="onLogoChange"
          />
        </div>

        <div class="sm:col-span-2 flex flex-wrap gap-6 pt-1">
          <label class="flex items-center gap-2 text-sm">
            <Switch v-model="form.goodForBusiness" /> Good for business
          </label>
          <label class="flex items-center gap-2 text-sm">
            <Switch v-model="form.luxury" /> Luxury
          </label>
          <label class="flex items-center gap-2 text-sm">
            <Switch v-model="form.subscription" /> Subscription
          </label>
        </div>

        <div v-if="logoPreview" class="sm:col-span-2 flex items-center gap-3">
          <img
            :src="logoPreview"
            class="object-contain w-12 h-12 border border-border bg-white rounded-lg"
          />
          <span class="text-xs text-muted-foreground">Logo preview</span>
        </div>
      </div>

      <p v-if="error" class="text-sm text-destructive">{{ error }}</p>
    </CardContent>

    <CardFooter class="justify-end gap-2">
      <Button variant="outline" @click="cancel">Cancel</Button>
      <Button :disabled="!canContinue" @click="next">
        Next stage
        <MoveRight class="w-4 h-4" />
      </Button>
    </CardFooter>
  </Card>
</template>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Button,
  Input,
  Label,
  Badge,
  Switch,
} from '@boogi/ui'
import { Search, Store, Check, MoveRight, Loader2, X } from 'lucide-vue-next'
import { addOfferWizard } from '@/stores/addOfferWizard'
import { wizardApi } from './wizardApi'

export default {
  name: 'step-retailer',
  mixins: [wizardApi],
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
    Button,
    Input,
    Label,
    Badge,
    Switch,
    Search,
    Store,
    Check,
    MoveRight,
    Loader2,
    X,
  },
  data() {
    return {
      wizard: addOfferWizard,
      mode: 'select',
      search: '',
      results: [],
      loadingSearch: false,
      hasSearched: false,
      searchTimer: null,
      error: '',
      logoPreview: '',
      form: {
        retailerName: '',
        retailerWebsite: '',
        monthlyWebVisits: null,
        goodForBusiness: false,
        luxury: false,
        subscription: false,
      },
    }
  },
  computed: {
    canContinue() {
      if (this.mode === 'select') return this.wizard.selectedRetailer
      return (
        this.form.retailerName.trim() !== '' && this.wizard.retailer.retailerLogoFile !== undefined
      )
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.runSearch, 350)
    },
  },
  methods: {
    async runSearch() {
      const term = this.search.trim()
      if (term.length < 2) {
        this.results = []
        this.hasSearched = false
        return
      }
      this.loadingSearch = true
      try {
        const page = await this.$api(
          this.byooPath(
            `/retailer-search?page=0&size=25&search=${encodeURIComponent(term)}&category=`
          )
        )
        this.results = (page?.content || []).map((item) => item.retailer)
      } catch (e) {
        this.error = e.message
      } finally {
        this.loadingSearch = false
        this.hasSearched = true
      }
    },
    selectRetailer(retailer) {
      this.error = ''
      // Clicking the already-selected retailer clears it.
      if (this.wizard.selectedRetailer && this.wizard.retailer.id === retailer.id) {
        this.wizard.deselectRetailer()
        return
      }
      this.wizard.setSelectedRetailer(retailer)
    },
    deselect() {
      this.error = ''
      this.wizard.deselectRetailer()
    },
    onLogoChange(event) {
      const file = event.target.files[0]
      if (!file) return
      this.wizard.selectedRetailer = false
      this.wizard.retailer.retailerLogoFile = file
      this.logoPreview = URL.createObjectURL(file)
    },
    cancel() {
      this.wizard.reset()
      this.$router.push('/custom-offers')
    },
    next() {
      this.error = ''
      if (this.mode === 'select') {
        if (!this.wizard.selectedRetailer) return
        // Existing retailer already created in DB — skip transaction test.
        this.$router.push('/add-offer/mids')
        return
      }
      // Creating a new retailer — capture the form and continue to enrichment.
      this.wizard.setCreatedRetailer({
        retailerName: this.form.retailerName.trim(),
        retailerWebsite: this.form.retailerWebsite.trim(),
        displayUrl: this.logoPreview,
        retailerLogoFile: this.wizard.retailer.retailerLogoFile,
        monthlyWebVisits: this.form.monthlyWebVisits,
        goodForBusiness: this.form.goodForBusiness,
        luxury: this.form.luxury,
        subscription: this.form.subscription,
        obCompatiable: undefined,
      })
      this.$router.push('/add-offer/transaction')
    },
  },
}
</script>
